import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Box, Text, HStack, VStack, Image, Button } from '@chakra-ui/react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { useConfig } from '../contexts/ConfigContext';
import { useSupabase } from '../contexts/SupabaseContext';
import { USER_TYPES, ADMIN_TABS, ROUTES, AdminTabType, API_ROUTES } from '../constants';
import { getCityLabel, getCountryLabel } from '../utils';
import Calendar from '../components/Calendar';

interface PendingUser {
  id: string;
  name: string;
  country: string;
  city: string;
  email?: string;
  phoneNumber?: string;
  userType: typeof USER_TYPES.INFLUENCER | typeof USER_TYPES.BUSINESS;
  status: string;
  image?: string;
  data?: any;
}

const AdminUsersListPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<AdminTabType>(ADMIN_TABS.INFLUENCERS);
  const navigate = useNavigate();
  const location = useLocation();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const { config } = useConfig();
  const { isAuthenticated } = useSupabase();
  const [users, setUsers] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Search state
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResult, setSearchResult] = useState<any>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [paidTillDateInput, setPaidTillDateInput] = useState<string>('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [savingPaidPeriod, setSavingPaidPeriod] = useState(false);
  const [paidPeriodSuccess, setPaidPeriodSuccess] = useState<string | null>(null);
  
  // Admin emails (fetched on mount to check if searched user is an admin)
  const [adminEmails, setAdminEmails] = useState<Set<string>>(new Set());
  
  // Cache key for sessionStorage
  const CACHE_KEY = 'adminUsersListCache';
  const SEARCH_RESULT_KEY = 'adminSearchResultCache';
  
  // Helper functions to manage cache
  const getCachedUsers = (tab: AdminTabType): PendingUser[] | null => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const cache = JSON.parse(cached);
        return cache[tab] || null;
      }
    } catch (e) {
      console.error('Error reading cache:', e);
    }
    return null;
  };

  const setCachedUsers = (tab: AdminTabType, users: PendingUser[]) => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      const cache = cached ? JSON.parse(cached) : { influencers: null, businesses: null };
      cache[tab] = users;
      sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (e) {
      console.error('Error writing cache:', e);
    }
  };

  const clearCachedUsers = (tab: AdminTabType) => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const cache = JSON.parse(cached);
        cache[tab] = null;
        sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
      }
    } catch (e) {
      console.error('Error clearing cache:', e);
    }
  };


  // Fetch pending users from backend
  const fetchPendingUsers = useCallback(async (forceReload: boolean = false, tab?: AdminTabType) => {
    const currentTab = tab || activeTab;
    
    // Only fetch if authenticated
    if (!isAuthenticated) {
      // Store return path and redirect to login
      const currentPath = window.location.pathname + window.location.search;
      if (!currentPath.includes('/login')) {
        sessionStorage.setItem('authReturnPath', currentPath);
      }
      navigate(ROUTES.LOGIN);
      return;
    }

    if (!config?.apiUrl) {
      setError('API URL not configured');
      setLoading(false);
      return;
    }

    // Use cached data if available and not forcing reload
    if (!forceReload) {
      const cached = getCachedUsers(currentTab);
      if (cached !== null) {
        setUsers(cached);
        setLoading(false);
        return;
      }
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${config.apiUrl}${API_ROUTES.ADMIN_PENDING_USERS}`, {
        params: {
          userType: currentTab === ADMIN_TABS.INFLUENCERS ? USER_TYPES.INFLUENCER : USER_TYPES.BUSINESS
        }
      });
      
      const pendingUsers: PendingUser[] = response.data.map((user: any) => {
        if (currentTab === ADMIN_TABS.INFLUENCERS) {
          return {
            id: user.id,
            name: [user.data.firstName, user.data.lastName].filter(Boolean).join(' ') || undefined,
            country: user.country,
            city: user.city,
            phoneNumber: user.data?.phoneNumber || user.phoneNumber,
            userType: USER_TYPES.INFLUENCER,
            status: user.status,
            image: user.data.photos?.[0] || '',
            data: user // Store full user object for navigation
          };
        } else {
          return {
            id: user.id,
            name: user.data.businessName,
            country: user.country,
            city: user.city,
            email: user.email,
            phoneNumber: user.data?.contactPerson?.phoneNumber,
            userType: USER_TYPES.BUSINESS,
            status: user.status,
            image: user.data.photos?.[0] || '',
            data: user // Store full user object for navigation
          };
        }
      });
      
      // Cache the results
      setCachedUsers(currentTab, pendingUsers);
      setUsers(pendingUsers);
    } catch (err: any) {
      console.error('Error fetching pending users:', err);
      setError(err.response?.data?.error || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, [activeTab, config?.apiUrl, isAuthenticated, navigate]);

  // Initial load or tab change - use cache if available
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    // If we have cached data, use it
    const cached = getCachedUsers(activeTab);
    if (cached !== null) {
      setUsers(cached);
      setLoading(false);
    } else {
      // Only fetch if we don't have cached data
      fetchPendingUsers(false, activeTab);
    }
  }, [activeTab, config?.apiUrl, isAuthenticated, fetchPendingUsers]);

  // Fetch admin emails on mount
  useEffect(() => {
    if (!isAuthenticated || !config?.apiUrl) return;

    const fetchAdminEmails = async () => {
      try {
        const response = await axios.get(`${config.apiUrl}/api/admin/admin-emails`);
        if (response.data?.emails) {
          setAdminEmails(new Set(response.data.emails.map((e: string) => e.toLowerCase().trim())));
        }
      } catch (err) {
        console.error('Failed to fetch admin emails:', err);
      }
    };
    fetchAdminEmails();
  }, [config?.apiUrl, isAuthenticated]);

  // Check if a user email is an admin email
  const isAdminUser = (email: string | undefined | null): boolean => {
    if (!email) return false;
    return adminEmails.has(email.toLowerCase().trim());
  };

  // Listen for cache removal events from detail pages
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CACHE_KEY && e.newValue) {
        try {
          const cache = JSON.parse(e.newValue);
          if (cache[activeTab] && Array.isArray(cache[activeTab])) {
            setUsers(cache[activeTab]);
          }
        } catch (e) {
          console.error('Error parsing cache update:', e);
        }
      }
    };

    // Listen for custom event for same-tab updates
    const handleCacheUpdate = (e: Event) => {
      const customEvent = e as CustomEvent<{ userId: string; tab: AdminTabType }>;
      const { tab, userId } = customEvent.detail;
      
      // Remove from cache
      try {
        const cached = sessionStorage.getItem(CACHE_KEY);
        if (cached) {
          const cache = JSON.parse(cached);
          if (cache[tab] && Array.isArray(cache[tab])) {
            cache[tab] = cache[tab].filter((user: PendingUser) => user.id !== userId);
            sessionStorage.setItem(CACHE_KEY, JSON.stringify(cache));
            // Update local state if we're on the same tab
            if (activeTab === tab) {
              setUsers(cache[tab]);
            }
          }
        }
      } catch (error) {
        console.error('Error removing user from cache:', error);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('adminUserRemoved', handleCacheUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('adminUserRemoved', handleCacheUpdate);
    };
  }, [activeTab]);

  // Handle return tab state
  React.useEffect(() => {
    if (location.state?.returnToTab) {
      setActiveTab(location.state.returnToTab);
    }
    const handleScroll = (e: Event) => {
      // Scroll handler for future use
    };
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [location.state?.returnToTab]);

  // Restore cached search result from sessionStorage on mount
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(SEARCH_RESULT_KEY);
      if (cached) {
        const result = JSON.parse(cached);
        setSearchResult(result);
        setSearchEmail(result.email || '');
        setPaidTillDateInput(result.data?.paidTillDate || '');
        setSelectedDate(result.data?.paidTillDate ? new Date(result.data.paidTillDate) : null);
      }
    } catch (e) {
      console.error('Failed to restore cached search result:', e);
    }
  }, []);

  const handleReload = () => {
    // Clear cache for current tab and reload
    clearCachedUsers(activeTab);
    fetchPendingUsers(true, activeTab);
  };

  const handleReview = (user: PendingUser) => {
    // Use full user object from data field if available, otherwise use simplified user
    const fullUserData = user.data || user;

    navigate(ROUTES.ADMIN_USER_PROFILE, {
      state: {
        userData: fullUserData,
        fromTab: activeTab,
        isAdminMode: true,
        userType: user.userType
      }
    });
  };

  // Search user by email
  const handleSearch = async () => {
    if (!config?.apiUrl || !searchEmail.trim()) return;

    try {
      setSearching(true);
      setSearchError(null);
      setSearchResult(null);

      const response = await axios.get(`${config.apiUrl}${API_ROUTES.ADMIN_SEARCH_USER}`, {
        params: { email: searchEmail.trim() }
      });

      const user = response.data;
      const userType = user.businessCategory === USER_TYPES.INFLUENCER ? USER_TYPES.INFLUENCER : USER_TYPES.BUSINESS;
      const result = { ...user, userType };
      setSearchResult(result);
      setPaidTillDateInput(user.data?.paidTillDate || '');
      setSelectedDate(user.data?.paidTillDate ? new Date(user.data.paidTillDate) : null);

      // Persist search result in sessionStorage so it survives navigation
      try {
        sessionStorage.setItem(SEARCH_RESULT_KEY, JSON.stringify(result));
      } catch (e) {
        console.error('Failed to cache search result:', e);
      }
    } catch (err: any) {
      setSearchError(err.response?.data?.error || 'Failed to search user');
    } finally {
      setSearching(false);
    }
  };

  // Save paid period for searched user
  const handleSavePaidPeriod = async () => {
    if (!config?.apiUrl || !searchResult?.id || !paidTillDateInput) return;

    try {
      setSavingPaidPeriod(true);
      setSearchError(null);
      setPaidPeriodSuccess(null);

      await axios.post(`${config.apiUrl}${API_ROUTES.ADMIN_SET_PAID_PERIOD}`, {
        userId: searchResult.id,
        paidTillDate: paidTillDateInput
      });

      // Update local search result
      const updatedResult = { ...searchResult, data: { ...(searchResult.data || {}), paidTillDate: paidTillDateInput } };
      setSearchResult(updatedResult);

      // Update cached search result in sessionStorage
      try {
        sessionStorage.setItem(SEARCH_RESULT_KEY, JSON.stringify(updatedResult));
      } catch (e) {
        console.error('Failed to update cached search result:', e);
      }

      // Clear profile cache so the affected user's PaidPeriodGuard re-fetches fresh data
      const clearUserProfileCache = (await import('../utils')).clearUserProfileCache;
      clearUserProfileCache(searchResult.id, searchResult.userType);

      // Show success feedback
      setPaidPeriodSuccess('Subscription date updated successfully');
      setTimeout(() => setPaidPeriodSuccess(null), 3000);
    } catch (err: any) {
      setSearchError(err.response?.data?.error || 'Failed to save paid period');
    } finally {
      setSavingPaidPeriod(false);
    }
  };

  // Calendar handlers
  const handlePaidDateSelect = (date: Date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const formatted = `${y}-${m}-${d}`;
    setPaidTillDateInput(formatted);
    setSelectedDate(date);
    setShowCalendar(false);
  };

  const handleCalendarClose = () => {
    setShowCalendar(false);
  };

  const handlePaidDateFieldClick = () => {
    setShowCalendar(true);
  };

  return (
    <Box
      className="base-container-long"
      ref={scrollContainerRef}
      data-scroll-container="true"
    >
      {/* Navigation Bar */}
      <Box
        className='edit-profiles-top'
        marginBottom="60px"
      >
        {activeTab !== ADMIN_TABS.SEARCH && (
          <Box
            width="38px"
            height="38px"
            border="1px solid #C6D959"
            borderRadius="50%"
            backgroundColor="rgba(198, 217, 89, 0.1)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            cursor="pointer"
            onClick={handleReload}
            _hover={{
              backgroundColor: "rgba(198, 217, 89, 0.2)"
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#C6D959"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2" />
            </svg>
          </Box>
        )}

        <Text
          className='title-text'
          marginLeft="24px"
        >
          Admin Panel
        </Text>
      </Box>

      {/* Subtitle */}
      <Box
        className='simple-box'
        top="104px"
      >
        <Text className='subtitle-text'>
          Pending Users
        </Text>
      </Box>

      {/* Divider */}
      <Box className="navigation-bar-divider"/>

      {/* Tab Navigation */}
      <Box
        position="absolute"
        top="161px"
        left="24px"
        right="24px"
        height="48px"
        display="flex"
        gap="8px"
        zIndex={10}
        overflow="visible"
      >
        {/* Influencers Tab */}
        <Button
          className='button-candidates-approved'
          backgroundColor={activeTab === ADMIN_TABS.INFLUENCERS ? '#C6D959' : '#1D1D1D'}
          color={activeTab === ADMIN_TABS.INFLUENCERS ? '#120F1B' : '#90909A'}
          onClick={() => setActiveTab(ADMIN_TABS.INFLUENCERS)}
        >
          Influencers
        </Button>

        {/* Businesses Tab */}
        <Button
          className='button-candidates-approved'
          backgroundColor={activeTab === ADMIN_TABS.BUSINESSES ? '#C6D959' : '#1D1D1D'}
          color={activeTab === ADMIN_TABS.BUSINESSES ? '#120F1B' : '#90909A'}
          onClick={() => setActiveTab(ADMIN_TABS.BUSINESSES)}
        >
          Businesses
        </Button>

        {/* Search Tab */}
        <Button
          className='button-candidates-approved'
          backgroundColor={activeTab === ADMIN_TABS.SEARCH ? '#C6D959' : '#1D1D1D'}
          color={activeTab === ADMIN_TABS.SEARCH ? '#120F1B' : '#90909A'}
          onClick={() => setActiveTab(ADMIN_TABS.SEARCH)}
        >
          Search
        </Button>
      </Box>

      {/* Users List */}
      <Box
        position="absolute"
        top="225px"
        left="24px"
        right="24px"
        bottom="24px"
        overflowY="auto"
        overflowX="hidden"
        zIndex={2}
        sx={{
          '&::-webkit-scrollbar': {
            display: 'none'
          },
          msOverflowStyle: 'none',
          scrollbarWidth: 'none'
        }}
      >
        {/* Pending Users List — only show on Influencers/Businesses tabs */}
        {activeTab !== ADMIN_TABS.SEARCH && loading && (
          <Box textAlign="center" padding="40px">
            <Text className='static-text'>Loading...</Text>
          </Box>
        )}
        
        {activeTab !== ADMIN_TABS.SEARCH && error && (
          <Box textAlign="center" padding="40px">
            <Text className='static-text' color="#F00C23">{error}</Text>
          </Box>
        )}

        {activeTab !== ADMIN_TABS.SEARCH && !loading && !error && users.length === 0 && (
          <Box textAlign="center" padding="40px">
            <Text className='static-text'>No pending users found</Text>
          </Box>
        )}

        {activeTab !== ADMIN_TABS.SEARCH && !loading && !error && users.length > 0 && (
          <VStack spacing="16px" align="stretch">
            {users.map((user) => (
              <Box
                className="vstack-main-box"
                key={user.id}
                height="139px"
              >
                {/* User Image - Left side */}
                <Box
                  position="absolute"
                  top="0"
                  left="0"
                  width="96px"
                  height="139px"
                  borderRadius="20px 0px 0px 20px"
                  overflow="hidden"
                  flexShrink={0}
                >
                  <Image
                    src={user.image || ''}
                    alt={user.name}
                    width="100%"
                    height="100%"
                    objectFit="cover"
                  />
                </Box>

                {/* Content Area - Right side */}
                <Box
                  position="absolute"
                  top="0"
                  left="96px"
                  right="0"
                  height="139px"
                  backgroundColor="#120F1B"
                  borderRadius="0px 20px 20px 0px"
                  padding="17px 0px 0px 11px"
                  display="flex"
                  flexDirection="column"
                >
                  {/* User Name */}
                  <Text
                    className='static-text-white'
                    marginBottom="8px"
                    maxWidth="100%"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {user.name}
                  </Text>

                  {/* Location */}
                  <Text
                    className='static-text'
                    marginBottom="4px"
                    maxWidth="100%"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {user.city && user.country
                      ? `${getCityLabel(user.city)}, ${getCountryLabel(user.country)}`
                      : user.country
                        ? getCountryLabel(user.country)
                        : user.city
                          ? getCityLabel(user.city)
                          : 'Location not specified'}
                  </Text>

                  {/* Contact Info */}
                  <Text
                    className='static-text'
                    marginBottom="16px"
                    maxWidth="100%"
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {user.phoneNumber || user.email || 'Contact info not available'}
                  </Text>

                  {/* Review Button */}
                  <HStack spacing="5px" position="absolute" bottom="10px" left="11px">
                    <Button
                      className='button-influencers-action'
                      backgroundColor="#C6D959"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleReview(user);
                      }}
                      _hover={{
                        backgroundColor: "#b8cc4a"
                      }}
                    >
                      Review
                    </Button>
                  </HStack>
                </Box>
              </Box>
            ))}
          </VStack>
        )}

        {/* Search Tab Content */}
        {activeTab === ADMIN_TABS.SEARCH && (
          <VStack spacing="20px" align="stretch" paddingTop="20px">
            {/* Search Input */}
            <Box>
              <Text className="subtitle-text" marginBottom="8px">
                Search by email
              </Text>
              <HStack spacing="8px">
                <Box flex="1">
                  <input
                    type="email"
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    placeholder="user@example.com"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearch();
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      backgroundColor: '#1C1C1E',
                      border: '1px solid #3A3A3C',
                      borderRadius: '8px',
                      color: '#FFFFFF',
                      fontSize: '14px',
                      outline: 'none',
                      boxSizing: 'border-box'
                    }}
                  />
                </Box>
                <Button
                  className="button-small"
                  onClick={handleSearch}
                  isLoading={searching}
                  isDisabled={!searchEmail.trim()}
                  minWidth="80px"
                >
                  Search
                </Button>
              </HStack>
            </Box>

            {/* Search Error */}
            {searchError && (
              <Text className="static-text" color="#F00C23">
                {searchError}
              </Text>
            )}

            {/* Search Result */}
            {searchResult && (
              <VStack spacing="16px" align="stretch">
                {/* User Info */}
                <Box
                  backgroundColor="#1C1C1E"
                  borderRadius="12px"
                  padding="16px"
                >
                  <VStack align="start" spacing="8px">
                    <Text className="title-text">
                      {searchResult.userType === USER_TYPES.INFLUENCER
                        ? searchResult.data?.firstName
                          ? `${searchResult.data.firstName} ${searchResult.data.lastName || ''}`.trim()
                          : 'Influencer'
                        : searchResult.data?.businessName || 'Business'}
                    </Text>
                    <Text className="static-text">
                      {searchResult.email}
                    </Text>
                    <Text className="static-text">
                      Type: {searchResult.userType === USER_TYPES.INFLUENCER ? 'Influencer' : 'Business'}
                    </Text>
                    <Text className="static-text">
                      Status: {searchResult.status || 'Unknown'}
                    </Text>
                    {/* Current subscription status — hidden for admin users */}
                    {!isAdminUser(searchResult.email) && (
                      <HStack spacing="8px" marginTop="4px">
                        <Box
                          width="8px"
                          height="8px"
                          borderRadius="50%"
                          backgroundColor={searchResult.data?.paidTillDate
                            ? new Date(searchResult.data.paidTillDate) >= new Date(new Date().toDateString())
                              ? '#C6D959'
                              : '#595C5D'
                            : '#595C5D'}
                        />
                        <Text className="subtitle-text" color="#90909A">
                          {searchResult.data?.paidTillDate
                            ? new Date(searchResult.data.paidTillDate) >= new Date(new Date().toDateString())
                              ? `Valid until ${new Date(searchResult.data.paidTillDate).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                              : 'Subscription expired'
                            : 'No active subscription'}
                        </Text>
                      </HStack>
                    )}
                  </VStack>
                </Box>

                {/* Paid Period Control — hidden for admin users */}
                {!isAdminUser(searchResult.email) && (
                <Box
                  backgroundColor="#1C1C1E"
                  borderRadius="12px"
                  padding="16px"
                  position="relative"
                  overflow="visible"
                >
                  <Text className="static-text-big-white" fontWeight="bold" marginBottom="8px">
                    Paid till date
                  </Text>
                  <HStack spacing="8px" align="flex-end" overflow="visible">
                    <Box flex="1" position="relative" overflow="visible">
                      <Box
                        width="100%"
                        padding="8px 12px"
                        backgroundColor="#120F1B"
                        border="1px solid #3A3A3C"
                        borderRadius="8px"
                        color={paidTillDateInput ? "#FFFFFF" : "rgba(255, 255, 255, 0.5)"}
                        fontSize="14px"
                        boxSizing="border-box"
                        cursor="pointer"
                        onClick={handlePaidDateFieldClick}
                        _hover={{
                          borderColor: "#C6D959"
                        }}
                      >
                        {paidTillDateInput
                          ? new Date(paidTillDateInput).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })
                          : 'Select date'}
                      </Box>

                      {showCalendar && (
                        createPortal(
                          <Box position="fixed" top="50%" left="50%" transform="translate(-50%, -50%)" zIndex="2000">
                            <Calendar
                              selectedDate={selectedDate}
                              onDateSelect={handlePaidDateSelect}
                              onClose={handleCalendarClose}
                              validationType="future"
                            />
                          </Box>,
                          document.body
                        )
                      )}
                    </Box>
                    <Button
                      className="button-small"
                      onClick={handleSavePaidPeriod}
                      isLoading={savingPaidPeriod}
                      isDisabled={!paidTillDateInput}
                      minWidth="80px"
                    >
                      Save
                    </Button>
                  </HStack>
                  {paidPeriodSuccess && (
                    <Text className="subtitle-text" color="#C6D959" marginTop="8px">
                      {paidPeriodSuccess}
                    </Text>
                  )}
                </Box>
                )}

                {/* Review Button - navigate to full profile */}
                <Button
                  className="button-big"
                  onClick={() => {
                    const fullUserData = searchResult;
                    navigate(ROUTES.ADMIN_USER_PROFILE, {
                      state: {
                        userData: fullUserData,
                        fromTab: ADMIN_TABS.SEARCH,
                        isAdminMode: true,
                        userType: searchResult.userType
                      }
                    });
                  }}
                  _hover={{
                    backgroundColor: "#b8cc4a"
                  }}
                >
                  View Full Profile
                </Button>
              </VStack>
            )}

            {/* Empty state */}
            {!searchResult && !searchError && !searching && (
              <Box textAlign="center" padding="40px">
                <Text className="static-text">Enter an email address to search for a user</Text>
              </Box>
            )}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default AdminUsersListPage;

